import React, {ChangeEvent, useEffect, useState} from "react";
import { TablePagination, Grid, Typography, Divider, Paper, Toolbar, LinearProgress, GridSize } from "@material-ui/core";
import { CanonicalRepresentationRequestInputData } from "src/model/CanonicalRepresentationRequestInputData";
import { useStyles } from "src/styles/ImageViewerStyles";
import { useStyles as tableUseStyles} from "src/styles/ResultTableStyles";
import ImageViewerItem from "../ImageViewer/ImageViewerItem";
import DownloadIconButton from "src/components/common/DownloadIconButton";
import { downloadCanonicalRepresentationsAsZip } from "src/utils/DownloadUtil";
import { CanonicalRepresentationOutputItem, ImageLocation } from "src/model/CanonicalRepresentationOutputItem";
import { CIRepMetadata } from "src/model/CIRepMetadata";
import { ImageInfo } from "src/model/ImageInfo";

type CIRepImageViewerProps = {
    canonicalRepresentationToDisplay: CanonicalRepresentationOutputItem | undefined,
    inputData: CanonicalRepresentationRequestInputData
}

/**
 * The Component is used to show the list of images of selected Canonical Representation
 */
const CIRepImageViewer: React.FunctionComponent<CIRepImageViewerProps> = (props: CIRepImageViewerProps) => {
    const styles = useStyles();
    const tableStyles = tableUseStyles();

    const imageStyle = {
        style: {
            maxHeight: "500px",
            maxWidth: "500px",
            width: "calc(100% - 20px)",
            height: "auto",
            paddingBottom: 20
        },
        itemXs: "auto" as GridSize
    };

    const [page, setPage] = useState(0);
    const [imagesPerPage, setImagesPerPage] = useState(5);
    const [displayProgressBar, setDisplayProgressBar] = useState(false);
    const [metadata, setMetadata] = useState<CIRepMetadata | undefined>();

    useEffect(() => {
        setPage(0);
        setImagesPerPage(5);
        setMetadata(undefined);
        if (props.canonicalRepresentationToDisplay) {
            fetchMetadata(props.canonicalRepresentationToDisplay.metadataLocation)
        }
    },[props.canonicalRepresentationToDisplay]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeImagesPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        setImagesPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const fetchMetadata = (metadataLocation: string) => {
        fetch(metadataLocation)
            .then(response => response.json())
            .then(metadata =>  {
                setMetadata(metadata);
            })
            .catch(error => {
                setMetadata(undefined);
                console.log('Error while retrieving canonical representation metadata for image viewer: ', error)
            })
    }

    const generateImageInfo = (metadata: CIRepMetadata | undefined, imageIndex: number): ImageInfo => {
        if (!metadata) {
            return {
                imageName: `CANONICAL-IMAGE-${imageIndex + 1}`
            }
        }

        let imagePrefix = "MAJOR";
        let imageNameIndex = imageIndex + 1;
        const majorImagesCount = metadata.major_cluster_indices.length
        let imageNote =
            `ClusterFrequency: ${(Math.round(metadata.cluster_frequencies[imageIndex] * 10000) / 100)
            .toFixed(2)
            .toString()}%`;

        if (imageIndex >= majorImagesCount) {
            imagePrefix = "MINOR";
            imageNameIndex = imageNameIndex - majorImagesCount;
        }

        return {
            imageName: `${imagePrefix}-IMAGE-${imageNameIndex}`,
            imageNote: imageNote
        }
    }

    let displayImages;
    if(props.canonicalRepresentationToDisplay) {
        let imageLocations: ImageLocation[] = props.canonicalRepresentationToDisplay.imageLocations;

        if (imageLocations.length > 0) {
            displayImages = (
                <Grid className={styles.imgViewerContainer} container spacing={3}>
                    {imageLocations
                        .slice(page * imagesPerPage, page * imagesPerPage + imagesPerPage)
                        .map(function(value, index) {
                            const imageIndex = page * imagesPerPage + index;
                            const imageInfo: ImageInfo = generateImageInfo(metadata, imageIndex);
                            return <ImageViewerItem
                                imgName={imageInfo.imageName}
                                imgUrl={value.canonicalImage}
                                imgStyle={imageStyle}
                                imgNote={imageInfo.imageNote} />
                        }
                    )}
                </Grid>
            );
        } else {
            displayImages = (
                <Typography className={`${styles.imageViewerMessage} ${styles.emptyImageViewerMessage}`} component="div">
                    There is no canonical image for this item, please reach out to ItemMatrix team.
                </Typography>
            );
        }
    } else {
        displayImages = (
            <Typography className={styles.imageViewerMessage} component="div">
                Please select a canonical item in the table to view included images.
            </Typography>
        );
    }

    const handleDownloadCapture = async () => {
        setDisplayProgressBar(true);
        await downloadCanonicalRepresentationsAsZip([props.canonicalRepresentationToDisplay!], props.inputData);
        setDisplayProgressBar(false);
    };

    return(
        <Paper className={styles.paper}>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <Toolbar variant="dense">
                        <Typography variant="subtitle1" className={styles.title} component="div">Image Viewer</Typography>
                        <div className={styles.flow}></div>
                        <DownloadIconButton
                            buttonTooltipText = "Download Selected Capture (.zip)"
                            disabled={!(props.canonicalRepresentationToDisplay && props.canonicalRepresentationToDisplay.imageLocations.length > 0)}
                            onDownload = {handleDownloadCapture}/>
                    </Toolbar>
                    {displayProgressBar && <LinearProgress className={styles.progressBar}/>}
                    <Divider></Divider>
                </Grid>
                <Grid item xs={12}>
                    {displayImages}
                    <Divider></Divider>
                    <TablePagination
                        className={tableStyles.pagination}
                        labelRowsPerPage="Images per page: "
                        rowsPerPageOptions={[5, 10, 50, 100, 200, 500, 1024]}
                        component="div"
                        count={(props.canonicalRepresentationToDisplay && props.canonicalRepresentationToDisplay.imageLocations) ? props.canonicalRepresentationToDisplay.imageLocations.length : 0}
                        rowsPerPage={imagesPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeImagesPerPage}
                    />
                </Grid>
            </Grid>
        </Paper>
    );
}

export default CIRepImageViewer;
